import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { FileInfo } from '../types/models';
import { apiClient } from '../clients/APIClient';
import { useToastStore } from './ToastStore';
import { useCollectionStore } from './collectionStore';
import { useDocumentSelectionStore } from './documentSelectionStore';

type UpdateDocumentContextParams = {
  operation: 'add' | 'remove' | 'delete' | 'refresh';
  documentIds?: string[];
  documents?: FileInfo[];
  targetCollectionId?: string;
  shouldRefreshServer?: boolean;
};

type DocumentState = {
  documents: FileInfo[];
  localFilePaths: Record<string, string>;
  setDocuments: (updater: FileInfo[] | ((prev: FileInfo[]) => FileInfo[])) => void;
  setLocalFilePath: (fileId: string, localPath: string) => void;
  getLocalFilePath: (fileId: string) => string | undefined;
  fetchDocuments: () => Promise<void>;
  removeDocument: (documentId: string) => Promise<void>;
  addDocument: (documentId: string) => void;
  isFileUploadAborted: boolean;
  isFileUploadAbortedRef: { current: boolean };
  setIsFileUploadAborted: (value: boolean) => void;
  handleFileUploadAbort: () => Promise<void>;
  isConnecting: boolean;
  initialLoad: boolean;
  attemptDataLoad: () => Promise<void>;
  updateDocumentContext: (params: UpdateDocumentContextParams) => Promise<void>;
};

const uploadAbortRef = { current: false };

const LOCAL_FILE_PATHS_KEY = 'edgerunner:localFilePaths';

const loadPersistedFilePaths = (): Record<string, string> => {
  try {
    const raw = localStorage.getItem(LOCAL_FILE_PATHS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const persistFilePaths = (paths: Record<string, string>) => {
  try {
    localStorage.setItem(LOCAL_FILE_PATHS_KEY, JSON.stringify(paths));
  } catch {
    /* quota or SSR — ignore */
  }
};

export const useDocumentStore = create<DocumentState>()(
  devtools(
    (set, get) => ({
      documents: [],
      localFilePaths: loadPersistedFilePaths(),

      setLocalFilePath: (fileId: string, localPath: string) => {
        set(
          (state) => {
            const updated = { ...state.localFilePaths, [fileId]: localPath };
            persistFilePaths(updated);
            return { localFilePaths: updated };
          },
          false,
          'documents/setLocalFilePath',
        );
      },

      getLocalFilePath: (fileId: string) => get().localFilePaths[fileId],

      setDocuments: (updater) =>
        set(
          (state) => ({
            documents:
              typeof updater === 'function'
                ? (updater as (p: FileInfo[]) => FileInfo[])(state.documents)
                : updater,
          }),
          false,
          'documents/setDocuments',
        ),

      fetchDocuments: async () => {
        const { initialLoad } = get();

        try {
          const files = await apiClient.files.listFiles();

          if (!Array.isArray(files)) {
            console.error('Error fetching files:', files);
            set({ documents: [] }, false, 'documents/fetchDocuments(error)');
            throw new Error('Failed to fetch documents');
          }

          set(
            (state) => {
              if (initialLoad && state.documents.length > 0) {
                const prevMap = new Map(state.documents.map((d) => [d.id, d]));

                files.forEach((newDoc: FileInfo) => {
                  const oldDoc = prevMap.get(newDoc.id);

                  if (!oldDoc) {
                    window.dispatchEvent(
                      new CustomEvent('search-index-update', {
                        detail: {
                          operation: 'add',
                          sourceType: 'file',
                          id: newDoc.id,
                          title: newDoc.filename,
                          lastModified: newDoc.created_at,
                          collectionId: newDoc.collection_id,
                        },
                      }),
                    );
                  } else if (oldDoc.filename !== newDoc.filename) {
                    window.dispatchEvent(
                      new CustomEvent('search-index-update', {
                        detail: {
                          operation: 'update',
                          sourceType: 'file',
                          id: newDoc.id,
                          title: newDoc.filename,
                          lastModified: newDoc.created_at,
                          collectionId: newDoc.collection_id,
                        },
                      }),
                    );
                  }
                });
              }

              return { documents: files };
            },
            false,
            'documents/fetchDocuments(success)',
          );
        } catch (err) {
          console.error('Error fetching documents:', err);
          set({ documents: [] }, false, 'documents/fetchDocuments(fail)');
          throw err;
        }
      },

      removeDocument: async (documentId: string) => {
        const { showToast } = useToastStore.getState();
        const { fetchDocuments } = get();
        const { fetchCollections, removeFilesFromAllCollections } = useCollectionStore.getState();
        const selection = useDocumentSelectionStore.getState();

        showToast('Deleting Document', 'loading', { timeout: 2000, placement: 'chat' });

        set(
          (state) => ({ documents: state.documents.filter((d) => d.id !== documentId) }),
          false,
          'documents/removeDocument(optimisticDocuments)',
        );

        removeFilesFromAllCollections([documentId]);
        selection.removeSelectedFiles([documentId]);

        selection.setSelectedCollections((prevSelectedCollections) => {
          const { collections } = useCollectionStore.getState();
          const { selectedFilesDoc } = useDocumentSelectionStore.getState();

          return prevSelectedCollections.filter((collectionId) => {
            const collection = collections.find((c) => c.id === collectionId);
            if (!collection) return false;

            const remainingFiles = collection.files.filter((f) => f.id !== documentId);
            if (remainingFiles.length === 0) return false;

            const remainingFileIds = remainingFiles.map((f) => f.id);
            return remainingFileIds.every((fileId) => selectedFilesDoc.includes(fileId));
          });
        });

        try {
          const res = await apiClient.files.deleteFileById(documentId);

          await Promise.all([fetchDocuments(), fetchCollections()]);
          showToast('Document Deleted', 'success', { timeout: 3000, placement: 'chat' });
          console.log('remove status: ', res);

          window.dispatchEvent(
            new CustomEvent('search-index-update', {
              detail: { operation: 'remove', sourceType: 'file', id: documentId },
            }),
          );
        } catch (err) {
          console.error(err);
          showToast('Error Deleting Document', 'error', { timeout: 3000, placement: 'chat' });
          await Promise.all([fetchDocuments(), fetchCollections()]);
        }
      },

      isFileUploadAborted: false,
      isFileUploadAbortedRef: uploadAbortRef,

      setIsFileUploadAborted: (value: boolean) =>
        set({ isFileUploadAborted: value }, false, 'documents/setIsFileUploadAborted'),

      handleFileUploadAbort: async () => {
        const { showToast } = useToastStore.getState();

        try {
          set({ isFileUploadAborted: true }, false, 'documents/handleFileUploadAbort(setTrue)');
          uploadAbortRef.current = true;

          showToast('Canceling files upload...', 'loading', { timeout: 5000, placement: 'chat' });

          const response = await apiClient.files.stopFileUpload();
          console.log('File upload stopped:', response);

          setTimeout(() => {
            uploadAbortRef.current = false;
            set({ isFileUploadAborted: false }, false, 'documents/handleFileUploadAbort(reset)');
            console.log('File upload abort flags reset');
          }, 3000);
        } catch (error) {
          console.error('Error stopping file upload:', error);
          uploadAbortRef.current = false;
          set({ isFileUploadAborted: false }, false, 'documents/handleFileUploadAbort(errorReset)');
        }
      },

      isConnecting: false,
      initialLoad: false,

      attemptDataLoad: async () => {
        const state = get();
        if (state.initialLoad || state.isConnecting) return;

        set({ isConnecting: true }, false, 'documents/attemptDataLoad(start)');

        const { fetchDocuments } = get();
        const { fetchCollections } = useCollectionStore.getState();

        try {
          await Promise.all([fetchDocuments(), fetchCollections()]);
          set({ initialLoad: true, isConnecting: false }, false, 'documents/attemptDataLoad(success)');
        } catch (error) {
          console.warn('Document/Collection load failed:', error);
          set({ isConnecting: false }, false, 'documents/attemptDataLoad(error)');
        }
      },

      updateDocumentContext: async (params: UpdateDocumentContextParams) => {
        const {
          operation,
          documentIds = [],
          documents: newDocuments = [],
          targetCollectionId,
          shouldRefreshServer = true,
        } = params;

        const { fetchDocuments } = get();
        const collectionsStore = useCollectionStore.getState();
        const selectionStore = useDocumentSelectionStore.getState();

        switch (operation) {
          case 'add': {
            if (newDocuments.length > 0) {
              set(
                (state) => {
                  const existingIds = new Set(state.documents.map((d) => d.id));
                  const uniqueNew = newDocuments.filter((d) => !existingIds.has(d.id));
                  return { documents: [...state.documents, ...uniqueNew] };
                },
                false,
                'documents/updateDocumentContext(addDocuments)',
              );

              collectionsStore.addFilesToCollections({
                targetCollectionId,
                documents: newDocuments,
              });
            }
            break;
          }

          case 'remove':
          case 'delete': {
            if (documentIds.length > 0) {
              set(
                (state) => ({
                  documents: state.documents.filter((d) => !documentIds.includes(d.id)),
                }),
                false,
                `documents/updateDocumentContext(${operation})/removeDocuments`,
              );

              collectionsStore.removeFilesFromAllCollections(documentIds);
              selectionStore.removeSelectedFiles(documentIds);

              selectionStore.setSelectedCollections((prevSelectedCollections) => {
                const { collections } = useCollectionStore.getState();
                const { selectedFilesDoc } = useDocumentSelectionStore.getState();

                return prevSelectedCollections.filter((collectionId) => {
                  const collection = collections.find((c) => c.id === collectionId);
                  if (!collection) return false;

                  const remainingFiles = collection.files.filter(
                    (f) => !documentIds.includes(f.id),
                  );
                  if (remainingFiles.length === 0) return false;

                  const remainingIds = remainingFiles.map((f) => f.id);
                  return remainingIds.every((fileId) => selectedFilesDoc.includes(fileId));
                });
              });
            }
            break;
          }

          case 'refresh':
          default:
            break;
        }

        if (shouldRefreshServer) {
          await Promise.all([fetchDocuments(), collectionsStore.fetchCollections()]);
        }
      },
    }),
    { name: 'useDocumentStore' },
  ),
);
